import { apiBaseUrl } from '../../backend/config.js';
import { connectWebSocket, socket, messageQueue } from './socketU.js';

let arInitialized = false;

// Loads external AR scripts if not already loaded
async function loadARScripts() {
  if (arInitialized) return;
  await loadScript('https://aframe.io/releases/1.2.0/aframe.min.js');
  await loadScript('https://cdn.jsdelivr.net/gh/jeromeetienne/AR.js@3.3.2/aframe/build/aframe-ar.min.js');
  arInitialized = true;
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      // Already loaded
      return resolve();
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script ${src}`));
    document.head.appendChild(script);
  });
}

async function initARScene(artwork) {
  // Remove existing scene if any
  const existingScene = document.querySelector('a-scene');
  if (existingScene) existingScene.remove();

  const container = document.getElementById('ar-container');
  if (!container) {
    console.error('AR container not found!');
    return;
  }

  // Clear container
  container.innerHTML = '';

  const sceneEl = document.createElement('a-scene');
  sceneEl.setAttribute('embedded', '');
  sceneEl.setAttribute('arjs', 'sourceType: webcam; patternRatio: 0.80; debugUIEnabled: false;');

  const lightEl = document.createElement('a-entity');
  lightEl.setAttribute('light', 'type: ambient; color: #ffffff; intensity: 1');
  sceneEl.appendChild(lightEl);

  // Marker
  const markerEl = document.createElement('a-marker');
  markerEl.setAttribute('type', 'pattern');
  markerEl.setAttribute('url', 'https://cdn.jsdelivr.net/gh/jeromeetienne/AR.js@3.3.2/three.js/examples/marker-training/examples/patterns/hiro.patt');

  let entityEl;
  const position = '0 0 0';

  // Handle artwork type
  switch (artwork.type) {
    case 'image':
      entityEl = document.createElement('a-image');
      entityEl.setAttribute('src', artwork.filePath);
      entityEl.setAttribute('position', position);
      entityEl.setAttribute('width', '1');
      entityEl.setAttribute('height', '1');
      break;

    case 'video':
      entityEl = document.createElement('a-video');
      entityEl.setAttribute('src', artwork.filePath);
      entityEl.setAttribute('position', position);
      entityEl.setAttribute('width', '1');
      entityEl.setAttribute('height', '1');
      break;

    case 'interactive':
    case 'AR':
    case '3D':
      // Assume a 3D model if provided, else a placeholder box
      if (artwork.filePath && artwork.filePath.endsWith('.glb')) {
        entityEl = document.createElement('a-entity');
        entityEl.setAttribute('gltf-model', artwork.filePath);
        entityEl.setAttribute('scale', '0.5 0.5 0.5');
        entityEl.setAttribute('position', position);
      } else {
        entityEl = document.createElement('a-box');
        entityEl.setAttribute('position', position);
        entityEl.setAttribute('color', '#4CC3D9');
      }
      break;

    default:
      // Default placeholder
      entityEl = document.createElement('a-box');
      entityEl.setAttribute('position', position);
      entityEl.setAttribute('color', '#FFC65D');
      break;
  }

  markerEl.appendChild(entityEl);
  sceneEl.appendChild(markerEl);

  const cameraEl = document.createElement('a-entity');
  cameraEl.setAttribute('camera', '');
  sceneEl.appendChild(cameraEl);

  container.appendChild(sceneEl);
}

async function fetchSingleArtwork(exhibitionId, artworkId) {
  const token = localStorage.getItem('token');
  if (!token) {
    alert('Not authenticated');
    return null;
  }

  const res = await fetch(`${apiBaseUrl}/api/hyper/exhibitions/${exhibitionId}/artworks/${artworkId}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!res.ok) {
    console.error('Failed to fetch single artwork', await res.text());
    return null;
  }

  return await res.json();
}

export async function showDisplayedArtwork(exhibitionId, artworkId) {
  console.log('showDisplayedArtwork called with:', exhibitionId, artworkId);
  
  const artwork = await fetchSingleArtwork(exhibitionId, artworkId);
  console.log('Fetched artwork:', artwork);

  if (!artwork) {
    console.error('No artwork returned from fetch');
    return;
  }

  const fullFilePath = artwork.filePath.startsWith('http')
  ? artwork.filePath
  : `${apiBaseUrl}${artwork.filePath}`;

  console.log('Resolved artwork file path:', fullFilePath);

  // Remove existing overlay if any
  const oldOverlay = document.getElementById('artwork-overlay');
  if (oldOverlay) oldOverlay.remove();

  const overlay = document.createElement('div');
  overlay.id = 'artwork-overlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100vw';
  overlay.style.height = '100vh';
  overlay.style.backgroundColor = 'rgba(0,0,0,0.8)';
  overlay.style.zIndex = '100000';
  overlay.style.display = 'flex';
  overlay.style.flexDirection = 'column';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';

  const artworkContainer = document.createElement('div');
  artworkContainer.style.backgroundColor = '#fff';
  artworkContainer.style.padding = '20px';
  artworkContainer.style.borderRadius = '8px';
  artworkContainer.style.maxWidth = '90%';
  artworkContainer.style.maxHeight = '90%';
  artworkContainer.style.overflow = 'auto';

  // Title and description
  const titleEl = document.createElement('h2');
  titleEl.textContent = artwork.title;
  artworkContainer.appendChild(titleEl);

  if (artwork.description) {
    const descEl = document.createElement('p');
    descEl.textContent = artwork.description;
    artworkContainer.appendChild(descEl);
  }

  // Determine type from API response or fallback to file extension
  const fileExtension = artwork.filePath.split('.').pop().toLowerCase();
  const type = artwork.type || (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension) ? 'image' : 'video');

  if (type === 'image') {
    const img = document.createElement('img');
    img.src = fullFilePath; // Ensure fullFilePath is resolved correctly
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    artworkContainer.appendChild(img);
  } else if (type === 'video') {
    const video = document.createElement('video');
    video.src = fullFilePath;
    video.controls = true;
    video.autoplay = true;
    video.style.maxWidth = '100%';
    video.style.height = 'auto';
    artworkContainer.appendChild(video);
  } else {
    const fallback = document.createElement('div');
    fallback.textContent = 'No preview available for this artwork.';
    fallback.style.color = '#000';
    artworkContainer.appendChild(fallback);
  }

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Close';
  closeBtn.style.marginTop = '10px';
  closeBtn.addEventListener('click', () => {
    overlay.remove();
  });
  artworkContainer.appendChild(closeBtn);

  overlay.appendChild(artworkContainer);
  document.body.appendChild(overlay);
}